/**
 * SignalChartDrawer (PULSE-231) — график-досье сигнала.
 *
 * Главная сцена: MarketChart (цена/OI/CVD/ликвидации) в окне вокруг
 * момента сигнала, сам сигнал размечен на каждом пейне (fire-маркер).
 * Если по сигналу открывались позиции — их вход/выход/TP/SL можно
 * наложить на график (клик по позиции в списке).
 *
 * Отсюда же: «Поделиться» (публичная ссылка /share/signal/:token),
 * переходы в полные метрики и decision-trace (старые дроверы).
 */
import { useEffect, useState } from 'react'
import { Check, LineChart, ListTree, Share2 } from 'lucide-react'

import { MarketChart, type TradeMarkers } from '@/components/market/MarketChart'
import { Drawer } from '@/components/ui/Drawer'
import {
  type SignalContextPosition,
  useCreateSignalShare,
  useSignalContext,
} from '@/hooks/useDashboardApi'
import { cn } from '@/lib/cn'

const WINDOWS = [
  { key: 'auto', label: 'авто' },
  { key: '1h', label: '1ч' },
  { key: '4h', label: '4ч' },
  { key: '24h', label: '24ч' },
]

const fmt = (v: number | null | undefined, d = 2) =>
  v == null ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(d).replace('-', '−')}`

function Chip({ label, value, tone }: {
  label: string
  value: string
  tone?: 'up' | 'dn' | 'fire' | null
}) {
  return (
    <span className="inline-flex items-baseline gap-1.5 bg-elevated border border-line rounded-md px-2.5 py-1">
      <span className="text-2xs text-textFaint">{label}</span>
      <span className={cn('font-mono text-xs font-semibold',
        tone === 'up' ? 'text-pulse' : tone === 'dn' ? 'text-rose'
          : tone === 'fire' ? 'text-fire' : 'text-text')}>
        {value}
      </span>
    </span>
  )
}

interface SignalChartDrawerProps {
  signalId: number | null
  label?: string
  onClose: () => void
  onOpenDetail?: () => void
  onOpenDecision?: () => void
}

export function SignalChartDrawer({
  signalId, label, onClose, onOpenDetail, onOpenDecision,
}: SignalChartDrawerProps) {
  const [window_, setWindow] = useState('auto')
  const [activePos, setActivePos] = useState<number | null>(null)
  const [copied, setCopied] = useState(false)
  const { data, isLoading, error } = useSignalContext(signalId, window_)
  const share = useCreateSignalShare()

  // сброс при смене сигнала
  useEffect(() => {
    setWindow('auto')
    setActivePos(null)
    setCopied(false)
  }, [signalId])

  const sig = data?.signal
  const positions = data?.positions ?? []
  const pos: SignalContextPosition | null =
    positions.find((p) => p.id === activePos)
    ?? positions.find((p) => !p.is_open)
    ?? positions[0]
    ?? null

  const markers: TradeMarkers | undefined = data ? {
    direction: (pos?.direction ?? sig?.direction ?? 'long') as string,
    signal: data.signal_marker,
    entry: pos?.entry_marker ?? null,
    exit: pos?.exit_marker ?? null,
    tp: pos?.take_profit_price ?? null,
    sl: pos?.stop_loss_price ?? null,
  } : undefined

  const onShare = async () => {
    if (!signalId) return
    const res = await share.mutateAsync(signalId)
    const url = `${location.origin}${res.url}`
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      window.prompt('Ссылка на сигнал:', url)
    }
  }

  const title = sig ? `${sig.symbol} · ${sig.signal_type}` : (label ?? 'Сигнал')
  const subtitle = sig
    ? `#${sig.id} · ${sig.timeframe ?? ''} · ${new Date(sig.ts).toLocaleString('ru')}`
    : undefined

  return (
    <Drawer
      open={signalId != null}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      widthClassName="w-[min(980px,100vw)]"
    >
      {isLoading && (
        <div className="py-16 text-center text-textFaint text-sm">загрузка контекста…</div>
      )}
      {!!error && (
        <div className="py-16 text-center text-rose text-sm">не удалось загрузить контекст сигнала</div>
      )}

      {data && sig && (
        <div className="space-y-4">
          {/* ── Чипы сигнала ── */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn('pill font-bold',
              sig.direction === 'short' ? 'bg-rose-faint text-rose' : 'bg-pulse-faint text-pulse')}>
              {(sig.direction ?? '—').toUpperCase()}
            </span>
            <Chip label="Δцена" value={`${fmt(sig.dp_pct)}%`}
              tone={(sig.dp_pct ?? 0) >= 0 ? 'up' : 'dn'} />
            <Chip label="ΔOI" value={`${fmt(sig.doi_pct)}%`}
              tone={(sig.doi_pct ?? 0) >= 0 ? 'up' : 'dn'} />
            <Chip label="score" value={fmt(sig.score, 1)} tone="fire" />
            {sig.rvol != null && <Chip label="rvol" value={fmt(sig.rvol, 1)} />}
            {sig.quality != null && <Chip label="quality" value={fmt(sig.quality, 1)} />}
            {sig.cvd_divergence_type && (
              <Chip label="CVD" value={String(sig.cvd_divergence_type)} />
            )}

            <span className="ml-auto flex items-center gap-1.5">
              <button
                onClick={onShare}
                disabled={share.isPending}
                className={cn('inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
                  copied
                    ? 'bg-pulse-faint text-pulse border-pulse'
                    : 'bg-elevated text-textDim border-line hover:text-text hover:border-lineHi')}
              >
                {copied ? <Check size={13} /> : <Share2 size={13} />}
                {copied ? 'ссылка скопирована' : 'поделиться'}
              </button>
            </span>
          </div>

          {/* ── Окно ── */}
          <div className="flex items-center gap-3">
            <div className="flex bg-surface border border-line rounded-md p-0.5 gap-0.5">
              {WINDOWS.map((w) => (
                <button key={w.key}
                  onClick={() => setWindow(w.key)}
                  className={cn('px-2.5 py-1 text-2xs font-medium rounded-sm transition-colors',
                    window_ === w.key
                      ? 'bg-elevated text-text shadow-soft'
                      : 'text-textFaint hover:text-textDim')}
                >{w.label}</button>
              ))}
            </div>
            <span className="text-2xs text-textFaint font-mono">tf {data.tf}</span>
          </div>

          {/* ── График ── */}
          <div className="h-[820px] bg-surface border border-line rounded-lg overflow-hidden">
            <MarketChart data={data.series} markers={markers} />
          </div>

          {/* ── Позиции по сигналу ── */}
          <div className="bg-surface border border-line rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 border-b border-line flex items-baseline gap-2">
              <h3 className="text-xs font-semibold">Позиции по сигналу</h3>
              <span className="text-2xs text-textFaint font-mono">{positions.length}</span>
              {positions.length > 1 && (
                <span className="text-2xs text-textFaint ml-auto">клик — показать сделку на графике</span>
              )}
            </div>
            {positions.length === 0 ? (
              <div className="px-4 py-4 text-xs text-textFaint">
                ни одна стратегия не вошла по этому сигналу
              </div>
            ) : (
              <table className="w-full text-xs">
                <tbody>
                  {positions.map((p) => {
                    const pnl = p.is_open ? p.unrealized_pnl_pct : p.realized_pnl_pct
                    const up = (pnl ?? 0) >= 0
                    const active = pos?.id === p.id
                    return (
                      <tr key={p.id}
                        onClick={() => setActivePos(p.id)}
                        className={cn('border-t border-line/50 cursor-pointer transition-colors',
                          active ? 'bg-elevated/80' : 'hover:bg-elevated/50')}>
                        <td className="px-4 py-2">
                          <span className="font-medium">{p.strategy_name ?? '—'}</span>
                          <span className="ml-2 text-2xs text-textFaint">{p.account_label}</span>
                          {p.is_real && (
                            <span className="ml-1.5 pill font-sans text-[9.5px] uppercase bg-fire-faint text-fire">реал</span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-textDim">
                          {p.entry_price ?? '—'} → {p.is_open ? '…' : (p.exit_price ?? '—')}
                        </td>
                        <td className={cn('px-3 py-2 text-right font-mono font-semibold',
                          up ? 'text-pulse' : 'text-rose')}>
                          {fmt(pnl, 2)}%{p.is_open && <span className="text-textFaint font-normal"> (откр.)</span>}
                        </td>
                        <td className="px-4 py-2 text-right text-2xs text-textFaint font-mono">
                          {p.exit_reason ?? ''}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Переходы ── */}
          <div className="flex gap-2">
            {onOpenDetail && (
              <button onClick={onOpenDetail}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border bg-elevated text-textDim border-line hover:text-text hover:border-lineHi transition-colors">
                <LineChart size={13} /> все метрики
              </button>
            )}
            {onOpenDecision && (
              <button onClick={onOpenDecision}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border bg-elevated text-textDim border-line hover:text-text hover:border-lineHi transition-colors">
                <ListTree size={13} /> решение матчера
              </button>
            )}
          </div>
        </div>
      )}
    </Drawer>
  )
}
